import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Hash, Type, Clock, Calendar, Target, User } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import Header from "../components/Header";
import RatingPanel from "../components/RatingPanel";
import { API } from "../App";
import { authFetch } from "../lib/auth";
import { toast } from "sonner";

const History = ({ user }) => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await authFetch(`${API}/training/history?limit=50`);
      if (response.ok) {
        const data = await response.json();
        setSessions(data);
      }
    } catch (error) {
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="history-page">
      <Header user={user} />

      <main className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Card key={i} className="bg-card border-border animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-6 bg-muted rounded w-1/3 mb-4"></div>
                    <div className="h-4 bg-muted rounded w-1/2"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : sessions.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="p-12 text-center">
                <Target className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-heading text-xl font-semibold mb-2">No Training Sessions Yet</h3>
                <p className="text-muted-foreground mb-6">
                  Complete your first training session to see your history here.
                </p>
                <div className="flex justify-center gap-4">
                  <Button
                    onClick={() => navigate("/train/numbers")}
                    className="bg-primary text-primary-foreground"
                  >
                    <Hash className="w-4 h-4 mr-2" />
                    Train Numbers
                  </Button>
                  <Button
                    onClick={() => navigate("/train/words")}
                    variant="outline"
                  >
                    <Type className="w-4 h-4 mr-2" />
                    Train Words
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {sessions.map((session, index) => (
                <motion.div
                  key={session.session_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card 
                    className="bg-card border-border card-hover"
                    data-testid={`history-item-${index}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        {/* Left: Mode & Details */}
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 flex items-center justify-center ${
                            session.mode === "numbers" ? "bg-primary/10" : 
                            session.mode === "names" ? "bg-orange-500/10" : "bg-secondary/10"
                          }`}>
                            {session.mode === "numbers" ? (
                              <Hash className="w-6 h-6 text-primary" />
                            ) : session.mode === "names" ? (
                              <User className="w-6 h-6 text-orange-500" />
                            ) : (
                              <Type className="w-6 h-6 text-secondary" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold capitalize">
                                {session.mode} Training
                              </h3>
                              {session.difficulty && (
                                <Badge variant="outline" className="capitalize text-xs">
                                  {session.difficulty}
                                </Badge>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatDate(session.created_at)}
                              </span>
                              {session.mode !== "working_memory" && session.memorize_seconds != null ? (
                                <span className="flex items-center gap-1 font-mono">
                                  <Clock className="w-3 h-3" />
                                  <span className="text-lime-400/80">Mem</span> {formatTime(session.memorize_seconds)}
                                  <span className="text-muted-foreground/40">·</span>
                                  <span className="text-blue-400/80">Recall</span> {formatTime(session.time_taken_seconds)}
                                </span>
                              ) : (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {formatTime(session.time_taken_seconds)}
                                </span>
                              )}
                              <span>
                                {session.items_count} {session.mode === "numbers" ? "digits" : session.mode === "names" ? "faces" : session.mode === "working_memory" ? "seconds" : "words"}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Right: recalled + rating */}
                        <div className="flex items-center gap-5">
                          <div className="text-center">
                            <div className="text-lg font-bold text-green-500" data-testid="history-recalled">
                              {Math.round((session.correct_credit ?? session.correct_count ?? 0) * 10) / 10}
                              <span className="text-muted-foreground/60"> / {session.items_count}</span>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {session.mode === "working_memory" ? "Rounds" : "Recalled"}
                            </p>
                          </div>
                          <div className="text-center">
                            <div className={`text-2xl font-bold ${
                              session.mode === "numbers" ? "text-primary" :
                              session.mode === "names" ? "text-orange-500" : "text-secondary"
                            }`} data-testid="history-rating">
                              {Math.round(session.rating ?? session.score)}
                            </div>
                            <div className="flex items-center gap-1 justify-center">
                              <p className="text-xs text-muted-foreground">Rating</p>
                              <RatingPanel
                                mode={session.mode}
                                itemsCount={session.items_count}
                                difficulty={session.difficulty}
                              />
                            </div>
                            {session.speed_adjusted === false && session.mode !== "working_memory" && (
                              <p className="text-[10px] text-amber-400/80 mt-1" data-testid="history-no-speed">
                                no speed adjustment
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default History;
